import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Container, Form, Row, Table } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { schedulingApi, eventTypeColors } from "../Components/schedulingApi";

export default function RegisterSchedulePage() {
  const { token } = useParams();
  const [link, setLink] = useState(null);
  const [events, setEvents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    enquiry_id: null,
    selected_event_ids: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadLink = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const data = await schedulingApi.getPublicRegisterLink(token);
      if (data?.error) {
        setError(data.error);
        return;
      }
      setLink(data.link || null);
      setEvents(data.events || []);
      setCourses(data.courses || []);
    } catch (e) {
      setError(e?.message || "Invalid or expired link");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadLink();
  }, [loadLink]);

  const handleStudentSubmit = (e) => {
    e.preventDefault();
    if (!form.first_name?.trim() || !form.last_name?.trim() || !form.email?.trim()) {
      toast.error("Please enter name and email.");
      return;
    }
    setStep(2);
  };

  const toggleEvent = (eventId) => {
    setForm((prev) => {
      const next = prev.selected_event_ids.includes(eventId)
        ? prev.selected_event_ids.filter((id) => id !== eventId)
        : [...prev.selected_event_ids, eventId];
      return { ...prev, selected_event_ids: next };
    });
  };

  const overlaps = (a, b) => {
    if (!a?.start || !a?.end || !b?.start || !b?.end) return false;
    return new Date(a.start) < new Date(b.end) && new Date(a.end) > new Date(b.start);
  };

  const eventsToShow = events.filter((ev) => {
    if (ev.full) return false;
    const selectedEvents = events.filter((e) => form.selected_event_ids.includes(e.id));
    if (selectedEvents.some((s) => s.id === ev.id)) return true;
    return !selectedEvents.some((s) => overlaps(ev, s));
  });

  const handleEnroll = async () => {
    if (form.selected_event_ids.length === 0) {
      toast.error("Select at least one event.");
      return;
    }
    setLoading(true);
    try {
      const data = await schedulingApi.publicEnroll(token, {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone_number: form.phone_number || undefined,
        event_ids: form.selected_event_ids,
      });
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      toast.success("You are registered for the selected events.");
      setStep(3);
    } catch (e) {
      toast.error(e?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !link) {
    return (
      <Container className="py-5 text-center">
        <p>Loading…</p>
      </Container>
    );
  }

  if (error && !link) {
    return (
      <Container className="py-5 text-center">
        <p className="text-danger">{error}</p>
      </Container>
    );
  }

  if (!link) {
    return null;
  }

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col md={8}>
          <h2 className="mb-2">{link.title || "Event Registration"}</h2>
          {link.description && <p className="text-muted">{link.description}</p>}

          {step === 1 && (
            <Form onSubmit={handleStudentSubmit} className="card card-body shadow-sm">
              <h5 className="mb-3">Your details</h5>
              <Form.Group className="mb-2">
                <Form.Label>First name *</Form.Label>
                <Form.Control
                  value={form.first_name}
                  onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Last name *</Form.Label>
                <Form.Control
                  value={form.last_name}
                  onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Email *</Form.Label>
                <Form.Control
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  value={form.phone_number}
                  onChange={(e) => setForm((p) => ({ ...p, phone_number: e.target.value }))}
                />
              </Form.Group>
              <p className="small text-muted">
                If you are already in our system as an enquiry, we will match by email. Otherwise you will be added as a new enquiry.
              </p>
              <Button type="submit" variant="primary">Next: Choose events</Button>
            </Form>
          )}

          {step === 2 && (
            <div className="card card-body shadow-sm">
              <h5 className="mb-3">Available events</h5>
              <p className="small text-muted mb-3">
                Select the events you want to register for. Full events are hidden. Events that conflict with your selection are excluded.
              </p>
              <Table bordered size="sm">
                <thead>
                  <tr>
                    <th></th>
                    <th>Event</th>
                    <th>Date</th>
                    <th>Room</th>
                    <th>Remaining seats</th>
                  </tr>
                </thead>
                <tbody>
                  {eventsToShow.map((ev) => (
                    <tr key={ev.id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={form.selected_event_ids.includes(ev.id)}
                          onChange={() => toggleEvent(ev.id)}
                          disabled={ev.full}
                        />
                      </td>
                      <td>
                        <span
                          style={{
                            backgroundColor: eventTypeColors[ev.event_type] || "#666",
                            color: "#fff",
                            padding: "2px 6px",
                            borderRadius: 4,
                            marginRight: 8,
                            fontSize: "0.8em",
                          }}
                        >
                          {ev.event_type}
                        </span>
                        {ev.title}
                      </td>
                      <td>{ev.start ? new Date(ev.start).toLocaleString() : "—"}</td>
                      <td>{ev.classroom?.room_code || "—"}</td>
                      <td>{ev.remaining_seats ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {eventsToShow.length === 0 && (
                <p className="text-muted">No available events in the current range.</p>
              )}
              <div className="d-flex justify-content-between mt-3">
                <Button variant="outline-secondary" onClick={() => setStep(1)}>Back</Button>
                <Button variant="primary" onClick={handleEnroll} disabled={form.selected_event_ids.length === 0}>
                  Register for selected events
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="card card-body shadow-sm text-center">
              <h5 className="text-success">Registration complete</h5>
              <p>You have been registered for the selected events. You will receive further details by email if configured.</p>
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
}
